import { on, emit, addMessage, getMessages } from "../../core/state.js";
import { getTheme, getGuidedFlow } from "../../core/configAccessors.js";
import { registry } from "../../core/registry.js";
import { getMessageRenderer } from "./messages/index.js";
import { attachScrollbar } from "../../services/scrollbar.js";
import { applyChatBackground } from "../../services/background.js";
import CustomScrollbar from "./CustomScrollbar.js";
import TypingIndicator from "./messages/TypingIndicator.js";

/** These should pin to the bottom (old scrollToBottom), not jump to prior .ws-fr. */
const SCROLL_TO_BOTTOM_KINDS = new Set([
  "build-box-cart",
  // "cart",
  "order-review",
  // "checkout-form",
  // "checkout-options",
]);

export default class MessageList {
  render(ctx) {
    const wrapper = document.createElement("div");
    wrapper.className = "ws-scroll-wrapper";

    const content = document.createElement("div");
    content.className = "ws-messages";

    const ScrollbarComp =
      registry.getComponent("CustomScrollbar") || CustomScrollbar;
    const { root: scrollbarRoot, refs } = new ScrollbarComp().render(ctx);

    wrapper.appendChild(content);
    wrapper.appendChild(scrollbarRoot);

    this._ctx = ctx;
    this._content = content;
    this._wrapper = wrapper;
    this._els = new Map();
    this._typingEl = null;
    // Cancels in-flight pin-top / pin-bottom settle loops when a newer one starts.
    this._scrollGen = 0;
    // Welcome + login greet must stay at scrollTop 0 so the promo/banner stay
    // visible (old #chatbot-container open behavior). Suppress auto-scroll while
    // that flow settles (images + account greet appends).
    this._suppressAutoScroll = false;
    // Briefly true right after _scrollToFirstResponse schedules its scroll, so
    // a typing indicator appearing in that window (see _scrollToFirstResponse)
    // doesn't race it to the bottom.
    this._holdBottomScroll = false;

    on("ws-message-added", (message) => this._appendMessage(message));
    on("ws-message-updated", ({ id, data }) => this._updateMessage(id, data));
    on("ws-message-removed", ({ id } = {}) => this._removeMessage(id));
    // scroll:true = new user turn (Shop Our Wines / input) — always pin to the
    // typing dots. Omitting scroll keeps the post-cards recommendations hold
    // so dots don't yank past the wine carousel (_holdBottomScroll).
    on("ws-chat-typing", ({ active, scroll } = {}) =>
      this._setTyping(active, { scroll: !!scroll }),
    );
    // Old widget opens at the top (banner visible). Do NOT pin to bottom on
    // welcome / first open — that hid .ws-scroll-top. Keep top while poster,
    // banner images, and the account login greet finish laying out.
    // Variants can override via ws-pin-scroll-to (e.g. Halleck /vineyard-tours/
    // focuses the vineyard card instead of the promo poster).
    on("ws-welcome-seeded", () => this._keepTopForWelcome());
    on("ws-pin-scroll-to", ({ selector, align } = {}) => {
      if (!selector) return;
      this._pinToSelectorWhileSettling(selector, { align, n: 16 });
    });
    // ⚠ SHARED/BASE — explicit "show me the newest message" request.
    // Note the rule just above: a plain reopen deliberately keeps the user's
    // scroll position. This is for the case where the transcript grew while the
    // panel was HIDDEN — the search bar runs a whole turn with the panel closed,
    // and every _scrollToBottom() during it was a no-op because a display:none
    // element has scrollHeight 0. Settling (not a single jump) because layout is
    // only real once the panel has been painted.
    on("ws-scroll-bottom", ({ settle = 12 } = {}) => {
      this._releaseWelcomeScrollHold();
      this._holdBottomScroll = false;
      clearTimeout(this._holdBottomScrollTimer);
      this._pinBottomWhileSettling(settle);
    });
    // Preserve the user's scroll position on reopen; do not jump to bottom.
    on("ws-messages-cleared", () => {
      this._setTyping(false);
      this._holdBottomScroll = false;
      clearTimeout(this._holdBottomScrollTimer);
      this._suppressAutoScroll = false;
      // The promo poster + banner live in a .ws-scroll-top band that ChatPanel
      // prepends into this scroll content. They're part of the layout, not the
      // transcript, so a refresh (clearMessages) must keep them — detach before
      // wiping, then re-prepend so the seeded welcome renders beneath them.
      const scrollTop = content.querySelector(".ws-scroll-top");
      content.innerHTML = "";
      if (scrollTop) content.appendChild(scrollTop);
      this._els.clear();
      this._seed();
    });

    if (getMessages().length === 0) {
      this._seed();
    } else {
      // Rehydrate without per-message auto-scroll. Welcome-only → stay at top
      // (banner visible). After the user has chatted → pin to latest message.
      getMessages().forEach((m) => this._appendMessage(m, { autoScroll: false }));
      if (getMessages().some((m) => m.role === "user")) {
        this._pinBottomWhileSettling();
      } else {
        this._keepTopForWelcome();
      }
    }

    this._bgDestroy = applyChatBackground(wrapper, content, ctx.config);

    this._scrollbarDestroy = attachScrollbar({
      content,
      wrapper,
      scrollbar: refs.scrollbar,
      track: refs.track,
      thumb: refs.thumb,
      btnUp: refs.btnUp,
      btnDown: refs.btnDown,
      btnTop: refs.btnTop,
      btnBottom: refs.btnBottom,
    });

    return wrapper;
  }

  _seed() {
    const welcome = getTheme("welcome_message", "Hello! How can I help you today?");

    // Accept both nested `theme.welcome` (offline stub) and flat
    // `theme.welcome_show_info_icon` + `theme.welcome_info_text` (admin-driven).
    // Flat admin keys take precedence; the nested `welcome` object is only a
    // fallback for offline stubs. DEFAULT_TENANT_CONFIG seeds a nested
    // `welcome: { show_info_icon: false }`, so reading nested-first would let
    // that default `false` shadow a tenant's flat `welcome_show_info_icon: "true"`
    // and suppress the icon for every admin-configured winery.
    const welcomeCfg = getTheme("welcome", null) || {};
    const showIconRaw = getTheme("welcome_show_info_icon", null) ?? welcomeCfg.show_info_icon;
    const infoText = getTheme("welcome_info_text", null) ?? welcomeCfg.info_text;
    const infoTitle = getTheme("welcome_info_title", null) ?? welcomeCfg.info_title;
    const showInfo =
      (showIconRaw === true || showIconRaw === "true") &&
      typeof infoText === "string" &&
      infoText.trim().length > 0;

    addMessage({
      role: "bot",
      kind: "text",
      // Welcome is not a turn response — scrolling it to start would hide the banner.
      isFirstResponse: false,
      data: { text: welcome },
      meta: {
        isWelcome: true,
        showInfoIcon: showInfo,
        infoText: showInfo ? infoText : null,
        infoTitle: showInfo ? infoTitle : null,
      },
    });

    const prompts = getGuidedFlow();
    if (prompts.length > 0) {
      addMessage({
        role: "bot",
        kind: "prompts",
        isFirstResponse: false,
        data: { items: prompts },
      });
    }

    // ⚠ SHARED/BASE — emitted for ALL tenants. It's a passive signal (no tenant
    // listens unless its variant opts in), so it's safe, but don't repurpose it
    // per-winery. Extensibility hook: fires every time the welcome + prompts are
    // seeded (first open AND on refresh/clearMessages). Variants append to the
    // welcome flow here — e.g. the Valle Commerce7 login form / returning-customer
    // greeting, mirroring the old addWelcomeMessageAndButtons() which showed the
    // login form inline whenever the welcome rendered (script.js 1683).
    emit("ws-welcome-seeded");
  }

  _appendMessage(message, { autoScroll = true } = {}) {
    const Renderer = getMessageRenderer(message.kind);
    if (!Renderer) return;
    const el = new Renderer().render(message, this._ctx);
    if (el) {
      this._content.appendChild(el);
      this._els.set(message.id, el);
      if (!autoScroll) return;
      // After header refresh, welcome hold pins scroll to top ~2s. A new user
      // turn must cancel that so typing dots aren't stuck below the fold.
      if (message.role === "user") {
        this._releaseWelcomeScrollHold();
        this._holdBottomScroll = false;
        this._scrollToBottom();
        return;
      }
      if (this._suppressAutoScroll) return;
      // Cart / checkout panels: always scroll to bottom (old showCartsProducts).
      // Default isFirstResponse would otherwise jump to the last .ws-fr (feature
      // cards) and leave the cart below the fold.
      if (SCROLL_TO_BOTTOM_KINDS.has(message.kind)) {
        this._scrollToBottom();
        this._pinBottomWhileSettling(8);
      } else if (message.isFirstResponse) {
        this._scrollToFirstResponse(el);
      }
    }
  }

  // Re-render a single message in place (token-by-token streaming).
  _updateMessage(id, data) {
    const oldEl = this._els.get(id);
    if (!oldEl) return;
    const message = getMessages().find((m) => m.id === id);
    if (!message) return;
    const Renderer = getMessageRenderer(message.kind);
    if (!Renderer) return;
    const newEl = new Renderer().render(message, this._ctx);
    if (!newEl) return;
    oldEl.replaceWith(newEl);
    this._els.set(id, newEl);
    if (this._suppressAutoScroll) return;

    // Wine-listing carousel re-rendering after a pagination click: the user
    // often scrolled down past the carousel to reach the page-number buttons,
    // so bring the (now-replaced) carousel back into view instead of leaving
    // the scroll wherever it was — but scroll to it directly, not to the
    // bottom of the whole transcript (old bug: jumped past the new page down
    // to "View Cart" / the follow-up prompts below it).
    if (message.kind === "cards") {
      requestAnimationFrame(() => {
        newEl.scrollIntoView({ block: "start", behavior: "smooth" });
      });
      return;
    }

    // Everything else pins to the bottom, same as it always did — a prompt
    // chip marking itself selected, a streaming bot-text bubble mid-stream,
    // etc. Streaming text only breaks from that once the stream completes
    // (payloadEnd), scrolling to the start of the just-finished reply instead
    // so a long response can be read from its top.
    if (message.kind === "text" && data.payloadEnd) {
      this._scrollToFirstResponse(newEl);
      return;
    }
    this._scrollToBottom();
  }

  // Drop a specific earlier message (old skipLoginForm parity — clearing
  // stale login/logout prompts once the opposite transition happens).
  _removeMessage(id) {
    if (!id) return;
    const el = this._els.get(id);
    if (el) el.remove();
    this._els.delete(id);
  }

  _scrollToBottom() {
    this._content.scrollTop = this._content.scrollHeight;
  }

  _scrollToTop() {
    this._content.scrollTop = 0;
  }

  // Scroll the newest bot response into view at its top edge instead of
  // snapping straight to the bottom — lets a long response be read from its
  // start. Takes the element directly from the caller (_appendMessage's `el`
  // / _updateMessage's `newEl`) rather than re-querying a ".ws-fr" marker
  // class: several message kinds that legitimately anchor a turn — checkout
  // steps like CheckoutForm/ShipAddressOptions/OrderReview's edit flows —
  // never added that class, so the query silently found some OLDER bubble
  // instead and scrolled there, leaving the actually-new content rendered
  // but off-screen with no visible sign anything happened.
  _scrollToFirstResponse(targetElement) {
    if (this._suppressAutoScroll) return;
    if (!targetElement) return;
    // chat-handler.js calls fetchRecommendations() right after emitting a card
    // response (e.g. "Here are some wines that you might like:" + the wine
    // carousel) to fetch a follow-up question. That flips the typing indicator
    // back on, and _setTyping's _scrollToBottom() fires synchronously — before
    // this rAF-deferred scroll below even runs — yanking the view past the
    // response we're about to scroll to. Hold the bottom-scroll off until this
    // one has actually happened.
    this._holdBottomScroll = true;
    requestAnimationFrame(() => {
      targetElement.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
      clearTimeout(this._holdBottomScrollTimer);
      this._holdBottomScrollTimer = setTimeout(() => {
        this._holdBottomScroll = false;
      }, 600);
    });
  }

  // Hold scrollTop at 0 while welcome/banner/login-greet layout settles.
  // Mirrors old open behavior: banner stays on screen; user scrolls down.
  _keepTopForWelcome() {
    this._suppressAutoScroll = true;
    this._pinTopWhileSettling(12);
    clearTimeout(this._welcomeScrollTimer);
    this._welcomeScrollTimer = setTimeout(() => {
      this._suppressAutoScroll = false;
    }, 2200);
  }

  // End welcome pin-top (refresh / first open). Called when the user starts a
  // turn so 3-dot typing + scroll-to-bottom work immediately after refresh.
  _releaseWelcomeScrollHold() {
    this._suppressAutoScroll = false;
    clearTimeout(this._welcomeScrollTimer);
    this._welcomeScrollTimer = null;
    // Bump gen so _pinTopWhileSettling / _pinToSelectorWhileSettling stop
    // yanking scrollTop back to 0 after we've pinned to the typing dots.
    this._scrollGen += 1;
  }

  /**
   * Cancel any in-flight top/bottom settle loop and pin a selector into view
   * while images settle. Used by Halleck /vineyard-tours/ so the vineyard card
   * (LEARN MORE) is focused instead of the promo poster (ss2 vs ss1).
   * align: "start" | "end" (default "end" — keep the CTA in the lower viewport).
   */
  _pinToSelectorWhileSettling(selector, { align = "end", n = 14 } = {}) {
    this._suppressAutoScroll = true;
    const gen = ++this._scrollGen; // cancels _pinTopWhileSettling / bottom settle
    clearTimeout(this._welcomeScrollTimer);
    this._welcomeScrollTimer = setTimeout(() => {
      this._suppressAutoScroll = false;
    }, Math.max(2200, n * 150 + 200));

    const scrollToEl = () => {
      const el = this._content.querySelector(selector);
      if (!el) return;
      const cRect = this._content.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      if (align === "start") {
        this._content.scrollTop = Math.max(
          0,
          this._content.scrollTop + (elRect.top - cRect.top) - 16
        );
      } else {
        // Align element bottom with the visible messages bottom (ss2: LEARN MORE in view).
        this._content.scrollTop = Math.max(
          0,
          this._content.scrollTop + (elRect.bottom - cRect.bottom) + 12
        );
      }
    };

    const tick = (left) => {
      if (gen !== this._scrollGen) return;
      scrollToEl();
      if (left > 0) setTimeout(() => tick(left - 1), 150);
    };
    requestAnimationFrame(() => tick(n));
  }

  _pinTopWhileSettling(n = 10) {
    const gen = ++this._scrollGen;
    const tick = (left) => {
      if (gen !== this._scrollGen) return;
      this._scrollToTop();
      if (left > 0) setTimeout(() => tick(left - 1), 150);
    };
    tick(n);
  }

  // Re-pin to the bottom ~10× over ~1.5s. Late layout shifts (poster/avatar
  // images loading after the initial render) otherwise leave the newest content
  // below the fold. Used for cart/checkout — not for welcome open.
  _pinBottomWhileSettling(n = 10) {
    this._suppressAutoScroll = false;
    const gen = ++this._scrollGen;
    const tick = (left) => {
      if (gen !== this._scrollGen) return;
      this._scrollToBottom();
      if (left > 0) setTimeout(() => tick(left - 1), 150);
    };
    tick(n);
  }

  // Show/hide the transient typing bubble. Driven by ws-chat-typing from the
  // chat handler; never added to the messages array. Idempotent on both edges.
  // opts.scroll — force pin to bottom (new user turn). Recommendations typing
  // after feature cards omits this so _holdBottomScroll still wins.
  _setTyping(active, { scroll = false } = {}) {
    if (active) {
      if (!this._typingEl) {
        this._typingEl = new TypingIndicator().render(this._ctx);
        this._content.appendChild(this._typingEl);
      }
      if (scroll) {
        // New user turn (incl. right after refresh) — cancel welcome pin-top
        // and show the 3-dot loader at the bottom.
        this._releaseWelcomeScrollHold();
        this._holdBottomScroll = false;
        clearTimeout(this._holdBottomScrollTimer);
        this._scrollToBottom();
        return;
      }
      if (this._suppressAutoScroll) return;
      if (!this._holdBottomScroll) this._scrollToBottom();
    } else if (this._typingEl) {
      this._typingEl.remove();
      this._typingEl = null;
    }
  }
}
